import {ChangeDetectorRef, EmbeddedViewRef, TemplateRef, ViewContainerRef} from '@angular/core';
import {BehaviorSubject, merge, Observable} from 'rxjs';
import {delay, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {ElementRulerDirective} from '../../element-ruler';
import {EmptyRowTemplateContext} from '../empty-row-template-context';

export class TableAvailableRowsObserver {

    public set emptyRow(value: any) {
        this.probeRowTemplateContext.$implicit = value;
    }

    public readonly availableRowCount$: Observable<number>;
    public readonly availableFullRowCount$: Observable<number>;

    private probeViewRef: EmbeddedViewRef<EmptyRowTemplateContext>;
    private probeRowElement: HTMLElement;

    private readonly rowHeight$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    private readonly probeRowTemplateContext: EmptyRowTemplateContext = {
        $implicit: {},
        empty: true
    };

    private rowCountCalculation$ = this.createRowCountCalculationStream();

    constructor(
        private viewContainerRef: ViewContainerRef,
        private templateRef: TemplateRef<EmptyRowTemplateContext>,
        private containerRulerDirective: ElementRulerDirective,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.availableRowCount$ = this.createAvailableRowCountStream();
        this.availableFullRowCount$ = this.createAvailableFullRowCountStream();
    }

    public setRowHeight(rowHeight: number): void {
        this.rowHeight$.next(rowHeight);
    }

    public initInstantly(emptyRow: any): void {
        this.probeRowTemplateContext.$implicit = emptyRow;

        this.createElement();
        this.detectRowHeight();
    }

    public createElement(): void {
        this.probeViewRef = this.viewContainerRef.createEmbeddedView(this.templateRef, this.probeRowTemplateContext);
        this.probeRowElement = this.probeViewRef.rootNodes
            .find((element: HTMLElement) => !!element.tagName);

        if (this.probeRowElement) {
            this.probeRowElement.style.visibility = 'hidden';
        }

        this.changeDetectorRef.detectChanges();
    }

    public detectRowHeight(): void {
        if (this.probeRowElement) {
            this.rowHeight$.next(this.probeRowElement.clientHeight);
        }

        this.removeElement();
    }

    private removeElement(): void {
        if (this.probeViewRef) {
            this.probeViewRef.destroy();
            this.probeViewRef = null;
            this.probeRowElement = null;

            this.changeDetectorRef.detectChanges();
        }
    }

    private getAvailableRowCount(containerHeight: number): number {
        return this.rowHeight$.value
            && Math.ceil(containerHeight / this.rowHeight$.value);
    }

    private getAvailableFullRowCount(containerHeight: number): number {
        return this.rowHeight$.value
            && Math.trunc(containerHeight / this.rowHeight$.value);
    }

    private createRowCountCalculationStream(): Observable<number> {
        const rowHeightDetected$ = this.rowHeight$.pipe(
            filter(rowHeight => !!rowHeight),
            map(() => this.containerRulerDirective.getSize())
        );

        return merge(
            rowHeightDetected$,
            this.containerRulerDirective.size$
        ).pipe(
            map(([, height]: [number, number]) => height)
        );
    }

    private createAvailableRowCountStream(): Observable<number> {
        return this.rowCountCalculation$.pipe(
            map(height => this.getAvailableRowCount(height)),
            distinctUntilChanged(),
            delay(0) // Breaks the loop: ResizeObserver -> Styles -> ResizeObserver.
        );
    }

    private createAvailableFullRowCountStream(): Observable<number> {
        return this.rowCountCalculation$.pipe(
            map(height => this.getAvailableFullRowCount(height)),
            distinctUntilChanged(),
            delay(0) // Breaks the loop: ResizeObserver -> Styles -> ResizeObserver.
        );
    }

}
